import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import axios from 'axios';

const ConsultationForm = () => {
  const [formData, setFormData] = useState({
    projectType: '',
    industry: '',
    name: '',
    email: '',
    company: '',
    phone: '',
    timeline: '',
    budget: '',
    teamSize: '',
    complexity: '',
    description: '',
    nda: false,
    newsletter: false
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const projectTypes = [
    { value: 'new-development', label: 'New Development', description: 'Build from scratch' },
    { value: 'system-integration', label: 'System Integration', description: 'Connect existing systems' },
    { value: 'technical-audit', label: 'Technical Audit', description: 'Review and optimize' },
    { value: 'digital-transformation', label: 'Digital Transformation', description: 'Complete modernization' }
  ];

  const industries = [
    { value: 'fintech', label: 'FinTech' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'saas', label: 'SaaS' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'startup', label: 'Startup' },
    { value: 'other', label: 'Other' }
  ];

  const timelines = [
    { value: '1-3-months', label: '1-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: '6-12-months', label: '6-12 Months' },
    { value: '12-months', label: '12+ Months' }
  ];

  const budgetRanges = [
    { value: '10k-50k', label: '$10k - $50k' },
    { value: '50k-100k', label: '$50k - $100k' },
    { value: '100k-250k', label: '$100k - $250k' },
    { value: '250k-500k', label: '$250k - $500k' },
    { value: '500k+', label: '$500k+' }
  ];

  const teamSizes = [
    { value: '1-2', label: '1-2 Developers' },
    { value: '3-5', label: '3-5 Developers' },
    { value: '6-10', label: '6-10 Developers' },
    { value: '10+', label: '10+ Developers' }
  ];

  const complexityLevels = [
    { value: 'simple', label: 'Simple', description: 'Basic functionality' },
    { value: 'moderate', label: 'Moderate', description: 'Standard features' },
    { value: 'complex', label: 'Complex', description: 'Advanced requirements' },
    { value: 'enterprise', label: 'Enterprise', description: 'Mission-critical systems' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Send email using the backend email service
      const response = await axios.post('/api/send-consultation-email', formData);
      
      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your consultation request! We will contact you within 24 hours.'
        });
        
        // Reset form
        setFormData({
          projectType: '',
          industry: '',
          name: '',
          email: '',
          company: '',
          phone: '',
          timeline: '',
          budget: '',
          teamSize: '',
          complexity: '',
          description: '',
          nda: false,
          newsletter: false
        });
        setCurrentStep(1);
      } else {
        throw new Error(response.data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your consultation request. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-text-primary mb-4">Project Details</h3>
        <p className="text-text-secondary mb-6">Tell us about your project to get personalized recommendations.</p>
      </div>

      <Select
        label="Project Type"
        description="What type of project are you looking to start?"
        options={projectTypes}
        value={formData.projectType}
        onChange={(value) => handleInputChange('projectType', value)}
        required
      />

      <Select
        label="Industry"
        description="Which industry best describes your business?"
        options={industries}
        value={formData.industry}
        onChange={(value) => handleInputChange('industry', value)}
        searchable
        required
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Select
          label="Timeline"
          options={timelines}
          value={formData.timeline}
          onChange={(value) => handleInputChange('timeline', value)}
          required
        />

        <Select
          label="Budget Range"
          options={budgetRanges}
          value={formData.budget}
          onChange={(value) => handleInputChange('budget', value)}
          required
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-text-primary mb-4">Contact Information</h3>
        <p className="text-text-secondary mb-6">How can we reach you for the consultation?</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Full Name"
          type="text"
          placeholder="John Doe"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="john@company.com"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          required
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Company Name"
          type="text"
          placeholder="Your Company"
          value={formData.company}
          onChange={(e) => handleInputChange('company', e.target.value)}
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          placeholder="+1 (555) 123-4567"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-text-primary mb-4">Project Scope</h3>
        <p className="text-text-secondary mb-6">Help us understand the complexity and requirements.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Select
          label="Team Size Needed"
          options={teamSizes}
          value={formData.teamSize}
          onChange={(value) => handleInputChange('teamSize', value)}
          required
        />

        <Select
          label="Complexity Level"
          options={complexityLevels}
          value={formData.complexity}
          onChange={(value) => handleInputChange('complexity', value)}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Project Description
        </label>
        <textarea
          className="w-full px-3 py-2 border border-border rounded-brand focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
          rows={4}
          placeholder="Describe your project, goals, and any specific requirements..."
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          required
        />
      </div>

      <div className="space-y-4">
        <Checkbox
          label="I would like to sign an NDA before discussing project details"
          checked={formData.nda}
          onChange={(e) => handleInputChange('nda', e.target.checked)}
        />

        <Checkbox
          label="Subscribe to our newsletter for development insights and industry updates"
          checked={formData.newsletter}
          onChange={(e) => handleInputChange('newsletter', e.target.checked)}
        />
      </div>
    </div>
  );

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-text-primary mb-4">
            Start Your Consultation
          </h2>
          <p className="text-lg text-text-secondary">
            Get personalized project insights and expert recommendations in just 3 steps.
          </p>
        </div>

        {/* Status Messages */}
        {submitStatus && (
          <div className={`mb-6 p-4 rounded-brand ${
            submitStatus.type === 'success' ?'bg-green-50 border border-green-200 text-green-800' :'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center space-x-2">
              <Icon 
                name={submitStatus.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
                size={20}
                className={submitStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}
              />
              <p>{submitStatus.message}</p>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-secondary text-white' :'bg-gray-200 text-text-secondary'
                }`}>
                  {step < currentStep ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    step
                  )}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step < currentStep ? 'bg-secondary' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-center mt-4">
            <div className="text-sm text-text-secondary">
              Step {currentStep} of 3: {
                currentStep === 1 ? 'Project Details' :
                currentStep === 2 ? 'Contact Information': 'Project Scope'
              }
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-brand p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Previous
            </Button>

            {currentStep < 3 ? (
              <Button
                type="button"
                variant="default"
                onClick={handleNext}
                iconName="ArrowRight"
                iconPosition="right"
                className="cta-primary"
              >
                Next Step
              </Button>
            ) : (
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
                className="cta-primary"
              >
                Submit Consultation Request
              </Button>
            )}
          </div>
        </form>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-6 text-sm text-text-secondary">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-green-500" />
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={16} className="text-green-500" />
              <span>Data Protected</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-secondary" />
              <span>24h Response</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationForm;